import { useEffect, useRef } from "react"
import { Circle, MapContainer, Marker, TileLayer, Tooltip } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"
import MarkerIcon from "leaflet/dist/images/marker-icon.png"
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
import { useGetListOfNearbyMarketsMutation } from "services/core/markets"

L.Icon.Default.mergeOptions({
  iconUrl: MarkerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
})

interface Props {
  adm0Code: number
  coordinates: [number, number] | undefined
}

const DEFAULT_CENTER: L.LatLngTuple = [0, 0]

const toLeafletCoordinates = (point: number[]): L.LatLngTuple | null => {
  if (point.length < 2) return null

  const [longitude, latitude] = point

  return [latitude, longitude]
}

export default function Map({ adm0Code, coordinates }: Props) {
  const [geoJSONList, { data }] = useGetListOfNearbyMarketsMutation()

  const mapRef = useRef<L.Map | null>(null)

  const nearbyMarkets =
    data?.features?.flatMap(feature => {
      const point = toLeafletCoordinates(feature.geometry.coordinates)

      if (!point) return []

      return [{ center: point, feature }]
    }) ?? []

  const mapPoints = coordinates
    ? [coordinates, ...nearbyMarkets.map(({ center }) => center)]
    : nearbyMarkets.map(({ center }) => center)

  useEffect(() => {
    if (!adm0Code) return
    geoJSONList({ adm0Code })
  }, [adm0Code, geoJSONList])

  useEffect(() => {
    if (!mapRef.current || !mapPoints.length) return

    if (mapPoints.length === 1) {
      mapRef.current.setView(mapPoints[0], 5)
      return
    }

    mapRef.current.fitBounds(L.latLngBounds(mapPoints).pad(0.2))
  }, [mapPoints])

  const center = mapPoints.length > 1 ? L.latLngBounds(mapPoints).getCenter() : mapPoints[0] ?? DEFAULT_CENTER
  const shouldRenderMap = Boolean(coordinates || nearbyMarkets.length)

  return shouldRenderMap ? (
    <MapContainer style={{ width: "100%", height: "350px" }} center={center} zoom={5} ref={mapRef}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />

      {coordinates && <Marker position={coordinates} />}

      {nearbyMarkets.map(({ center: marketCenter, feature }, index) => (
        <Circle
          key={feature.properties?.id ?? index}
          center={marketCenter}
          pathOptions={{
            color: "#085387",
            fillOpacity: 1,
            fillColor: "#f7b825",
            weight: 7,
          }}
          radius={50}
        >
          <Tooltip>{feature.properties?.name ?? ""}</Tooltip>
        </Circle>
      ))}
    </MapContainer>
  ) : null
}
